import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Star, IndianRupee, CheckCircle, XCircle, 
  User, Settings, Bell, TrendingUp, FileText, Eye, 
  ArrowRight, Phone, MapPin, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PanditDashboardPage({ user }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Action dialogs
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error('Please login to access dashboard');
      navigate('/auth');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch profile
      const profileRes = await axios.get(`${API}/pandit/my-profile`, { headers });
      if (!profileRes.data) {
        toast.info('Please complete your pandit registration');
        navigate('/pandit/register');
        return;
      }
      setProfile(profileRes.data);

      // Fetch stats
      const statsRes = await axios.get(`${API}/pandit/dashboard/stats`, { headers });
      setStats(statsRes.data);

      // Fetch bookings
      const bookingsRes = await axios.get(`${API}/pandit/bookings/pandit`, { headers });
      setBookings(bookingsRes.data);

      // Fetch earnings
      try {
        const earningsRes = await axios.get(`${API}/payments/pandit-earnings`, { headers });
        setEarnings(earningsRes.data);
      } catch {
        // Earnings may not exist yet
      }
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/pandit/register');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBookingAction = async () => {
    if (!selectedBooking || !actionType) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.put(`${API}/pandit/booking/${selectedBooking.id}/status`, {
        status: actionType,
        rejection_reason: actionType === 'rejected' ? rejectionReason : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Booking ${actionType} successfully`);
      setSelectedBooking(null);
      setActionType(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setActionLoading(false);
    }
  };

  const filterBookings = (status) => {
    if (status === 'all') return bookings;
    return bookings.filter(b => b.status === status);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={`${styles[status]} hover:${styles[status]}`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-3 animate-pulse">ॐ</div>
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                Pandit Dashboard
              </h1>
              <p className="text-orange-100 text-sm mt-1">
                Welcome, {profile?.name || user?.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => navigate('/pandit/register')}
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 -mt-4">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats?.total_bookings || 0}</div>
                <div className="text-xs text-gray-500">Total Bookings</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats?.pending_bookings || 0}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats?.completed_bookings || 0}</div>
                <div className="text-xs text-gray-500">Completed</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">₹{stats?.total_earnings?.toLocaleString() || 0}</div>
                <div className="text-xs text-gray-500">Earnings</div>
              </div>
            </div>
          </div>
        </div>

        {/* Earnings Card */}
        {earnings && earnings.total_transactions > 0 && (
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-4 text-white mt-4" data-testid="earnings-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> My Earnings
              </h3>
              <Badge className="bg-white/20 text-white text-xs hover:bg-white/30">{earnings.commission_rate} platform fee</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-2xl font-bold">₹{earnings.total_earnings?.toLocaleString('en-IN')}</p>
                <p className="text-xs text-green-100">Net Earnings</p>
              </div>
              <div>
                <p className="text-2xl font-bold">₹{earnings.total_booking_value?.toLocaleString('en-IN')}</p>
                <p className="text-xs text-green-100">Gross Bookings</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{earnings.total_transactions}</p>
                <p className="text-xs text-green-100">Paid Bookings</p>
              </div>
            </div>
            {earnings.recent_transactions?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-xs text-green-100 mb-2">Recent Payments</p>
                {earnings.recent_transactions.slice(0, 3).map((tx, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1">
                    <span className="text-green-100">{tx.pooja_type || 'Pooja'} — {tx.devotee_name}</span>
                    <span className="font-semibold">₹{tx.earning?.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Summary */}
        <div className="bg-white rounded-xl p-4 shadow-sm mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src={profile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.name || 'P')}&background=FF9933&color=fff&size=60`}
                alt={profile?.name}
                className="w-14 h-14 rounded-xl object-cover"
              />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{profile?.name}</h3>
                  {profile?.verified && (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Verified</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {profile?.city}
                  </span>
                  <span className="flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                    {stats?.rating?.toFixed(1) || 'New'}
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/pandit/${profile?.id}`)}
            >
              <Eye className="w-4 h-4 mr-1" />
              View Profile
            </Button>
          </div>
        </div>

        {/* Get Verified Banner */}
        {profile && !profile.verified && (
          <div
            className="mt-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-4 text-white cursor-pointer hover:from-orange-600 hover:to-amber-600 transition-all"
            onClick={() => navigate('/pandit/verify')}
            data-testid="get-verified-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-sm">Get DharmBook Certified</p>
                  <p className="text-xs text-white/80">Pass the exam & verify your profile to earn the trusted badge</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 flex-shrink-0" />
            </div>
          </div>
        )}

        {/* Bookings */}
        <div className="bg-white rounded-xl shadow-sm mt-4 overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Bookings</h2>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
              {['pending', 'accepted', 'completed', 'all'].map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF9933] data-[state=active]:text-[#FF9933] px-4 py-3 capitalize"
                >
                  {tab}
                  {tab === 'pending' && stats?.pending_bookings > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                      {stats.pending_bookings}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="p-4">
              {filterBookings(activeTab === 'all' ? 'all' : activeTab).length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No {activeTab} bookings</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filterBookings(activeTab === 'all' ? 'all' : activeTab).map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      onAccept={() => {
                        setSelectedBooking(booking);
                        setActionType('accepted');
                      }}
                      onReject={() => {
                        setSelectedBooking(booking);
                        setActionType('rejected');
                      }}
                      onComplete={() => {
                        setSelectedBooking(booking);
                        setActionType('completed');
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </Tabs>
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!selectedBooking && !!actionType} onOpenChange={() => {
        setSelectedBooking(null);
        setActionType(null);
        setRejectionReason('');
      }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accepted' && 'Accept Booking'}
              {actionType === 'rejected' && 'Reject Booking'}
              {actionType === 'completed' && 'Mark as Completed'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {actionType === 'accepted' && (
              <p className="text-gray-600">
                Are you sure you want to accept this booking for {selectedBooking?.pooja_type} on {selectedBooking?.booking_date}?
              </p>
            )}
            
            {actionType === 'rejected' && (
              <div className="space-y-3">
                <p className="text-gray-600">Please provide a reason for rejection (optional):</p>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Not available on this date"
                  rows={3}
                  className="rounded-xl"
                />
              </div>
            )}
            
            {actionType === 'completed' && (
              <p className="text-gray-600">
                Mark this pooja as completed? The devotee will be able to leave a review.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedBooking(null);
                setActionType(null);
              }}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBookingAction}
              disabled={actionLoading}
              className={`rounded-xl ${
                actionType === 'rejected' ? 'bg-red-500 hover:bg-red-600' : 'bg-[#FF9933] hover:bg-[#E68A00]'
              }`}
            >
              {actionLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingCard({ booking, onAccept, onReject, onComplete }) {
  const navigate = useNavigate();
  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-700',
    accepted: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-700'
  };

  return (
    <div className="border rounded-xl p-4 hover:border-orange-200 transition-colors" data-testid={`booking-${booking.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-gray-900">{booking.pooja_type}</h4>
            <Badge className={`${statusStyles[booking.status]} hover:${statusStyles[booking.status]} text-xs`}>
              {booking.status}
            </Badge>
          </div>
          
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {booking.booking_date}
              </span>
              {booking.booking_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {booking.booking_time}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {booking.devotee_name}
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {booking.city}, {booking.state}
            </div>
          </div>
          
          <div className="mt-2 font-semibold text-[#FF9933]">
            ₹{booking.agreed_fee}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {booking.status === 'pending' && (
            <>
              <Button
                size="sm"
                className="rounded-full bg-green-500 hover:bg-green-600 text-xs px-4"
                onClick={onAccept}
                data-testid={`accept-${booking.id}`}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full text-red-500 border-red-200 hover:bg-red-50 text-xs px-4"
                onClick={onReject}
                data-testid={`reject-${booking.id}`}
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Reject
              </Button>
            </>
          )}
          {booking.status === 'accepted' && (
            <Button
              size="sm"
              className="rounded-full bg-[#FF9933] hover:bg-[#E68A00] text-xs px-4"
              onClick={onComplete}
              data-testid={`complete-${booking.id}`}
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" />
              Complete
            </Button>
          )}
          <a
            href={`tel:${booking.devotee_phone}`}
            className="flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-[#FF9933]"
          >
            <Phone className="w-3.5 h-3.5" />
            Call
          </a>
          {['accepted', 'completed'].includes(booking.status) && (
            <button
              onClick={() => navigate(`/pandit/chat?booking=${booking.id}`)}
              className="flex items-center justify-center gap-1 text-xs text-orange-500 hover:text-orange-600"
              data-testid={`pandit-chat-btn-${booking.id}`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Chat
            </button>
          )}
        </div>
      </div>
      
      {booking.special_requirements && (
        <div className="mt-3 pt-3 border-t text-sm text-gray-600">
          <strong>Note:</strong> {booking.special_requirements}
        </div>
      )}
    </div>
  );
}
